// Lógica pura de «Ahora» (Centro de Alertas, F3): la bandeja de todos los clientes. Sin React, para
// testearla sin montar nada.
//
// Reusa la de la pantalla de Alertas del cliente (`clientAlertsLogic`): un chip filtra igual en las dos,
// y los números de un cliente salen de los mismos ítems que su pantalla.
import {alertsForFilter, alertsSummary} from '../../client/panel_v11/alerts/clientAlertsLogic';
import {nextResolved, withResolvedRows} from '../shared/recalculation';

// El `indicator` de una alerta disparada por una regla del Centro (los de sistema llevan el suyo).
export const ALERT_RULE_INDICATOR = 'alert_rule';

function clientsOf(payload) {
    return payload && Array.isArray(payload.clients) ? payload.clients : [];
}

function itemsOf(client) {
    return client && Array.isArray(client.items) ? client.items : [];
}

export function allItems(payload) {
    return clientsOf(payload).flatMap(itemsOf);
}

/** El id de la regla que disparó la alerta (como texto), o `null` si no es de una regla. */
export function ruleIdOf(item) {
    if (!item || item.indicator !== ALERT_RULE_INDICATOR || !item.detail) return null;
    const id = item.detail.rule_id;
    return id === null || id === undefined || id === '' ? null : String(id);
}

function matchesRule(item, ruleId) {
    return ruleId === null || ruleIdOf(item) === ruleId;
}

function matchesClient(client, clientId) {
    return clientId === null || String(client.client_id) === clientId;
}

// Los clientes que deja el filtro «Por cliente» (todos sin él).
function clientsIn(payload, clientId) {
    return clientsOf(payload).filter((client) => matchesClient(client, clientId));
}

/** `?regla=` válido: el id numérico de una regla personalizada (lo que arma «Ver qué disparó»). */
export function ruleFromParam(value) {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    return /^\d+$/.test(text) ? text : null;
}

/** `?cliente=` válido: el id numérico de un cliente («Por cliente»). */
export function clientFromParam(value) {
    return ruleFromParam(value);
}

/**
 * Las reglas que hoy tienen alertas en la bandeja, `[{id, name, active}]` por nombre: lo que ofrece el
 * chip «Por regla». `active` cuenta las vigentes (no silenciadas), como el resto de los chips. Con un
 * cliente elegido, sólo las de ese cliente: el chip no puede ofrecer una regla que deja la lista vacía.
 *
 * `currentNames` (`{id: nombre}`, de Mis reglas) manda sobre el nombre de la alerta: ése es una foto de
 * la Bitácora del día que disparó, y una regla renombrada después lo tendría viejo (C-ac-fe-ahora-07).
 */
export function rulesInTray(payload, currentNames = {}, clientId = null) {
    const byId = new Map();
    clientsIn(payload, clientId).flatMap(itemsOf).forEach((item) => {
        const id = ruleIdOf(item);
        if (id === null) return;
        const entry = byId.get(id) || {id, name: currentNames[id] || null, active: 0};
        if (!entry.name && item.detail.rule_name) entry.name = item.detail.rule_name;
        if (!item.snoozed) entry.active += 1;
        byId.set(id, entry);
    });
    return [...byId.values()].sort((a, b) => (a.name || '').localeCompare(b.name || '') || a.id.localeCompare(b.id));
}

/**
 * Los clientes que hoy tienen alertas en la bandeja, `[{id, name, active, snoozed}]` por nombre: lo que
 * ofrece el chip «Por cliente». Con una regla elegida, sólo los clientes con alertas de esa regla, y los
 * números son los de esa regla.
 *
 * Sin regla, los números son los del backend (`count`, `snoozed_count`: la pill del cliente en el Listado),
 * que cuentan también lo que el tope (`truncated`) dejó afuera de la lista.
 */
export function clientsInTray(payload, ruleId = null) {
    return clientsOf(payload)
        .map((client) => {
            const items = itemsOf(client).filter((item) => matchesRule(item, ruleId));
            const fromRows = ruleId !== null;
            const active = fromRows || !Number.isFinite(Number(client.count))
                ? items.filter((item) => !item.snoozed).length : Number(client.count);
            const snoozed = fromRows || !Number.isFinite(Number(client.snoozed_count))
                ? items.filter((item) => item.snoozed).length : Number(client.snoozed_count);
            return {id: String(client.client_id), name: client.client_name || '', active, snoozed, rows: items.length};
        })
        .filter((client) => client.rows > 0)
        .map(({rows, ...client}) => client)
        .sort((a, b) => a.name.localeCompare(b.name, 'es', {sensitivity: 'base'}) || a.id.localeCompare(b.id));
}

const EMPTY_CHIPS = {active: 0, critic: 0, today: 0, repeated: 0, snoozed: 0, snoozed_this_week: 0};

// Los conteos del backend (`chips`, `chips_by_rule[id]`) con los nombres de `alertsSummary`.
function summaryFromBackend(chips) {
    const counts = {...EMPTY_CHIPS, ...(chips || {})};
    return {
        active: counts.active, critic: counts.critic, today: counts.today, repeated: counts.repeated,
        snoozed: counts.snoozed, snoozedThisWeek: counts.snoozed_this_week,
    };
}

/**
 * Los números de la pantalla.
 *
 * - `kpis`: los cuatro de arriba, sobre TODO (los filtros no los mueven). Vigentes es la suma de las pills
 *   de cada cliente. Suman `clientsWithActive` y `clientsTotal` («en N de tus M»).
 * - `chips`: los contadores de los chips, sobre lo que alcanzan la regla y el cliente elegidos: prender un
 *   chip no puede mostrar otro número que las filas que deja.
 *
 * Con `truncated` (el tope de ítems del backend) las filas no alcanzan para contar: lo recortado son
 * justamente las silenciadas y lo menos grave. Ahí KPIs y chips salen de `chips` / `chips_by_rule`, que el
 * backend cuenta antes del tope con la misma definición (review C-ac-fe-ahora-05, opción b de fede). Sin esos
 * campos (backend viejo), Vigentes y Silenciadas salen de `count` / `snoozed_count` y el resto de las filas.
 * El backend no cuenta los chips por cliente: con un cliente elegido salen de sus filas, salvo Vigentes y
 * Silenciadas sin regla, que son las de su pill (`count`, `snoozed_count`).
 */
export function nowSummary(payload, ruleId = null, clientId = null, today = new Date()) {
    const items = allItems(payload);
    const scoped = clientsIn(payload, clientId).flatMap(itemsOf).filter((item) => matchesRule(item, ruleId));
    let kpis = alertsSummary({items}, today);
    let chips = alertsSummary({items: scoped}, today);
    if (payload && payload.truncated) {
        if (payload.chips) {
            kpis = summaryFromBackend(payload.chips);
            if (clientId === null) {
                chips = ruleId === null ? summaryFromBackend(payload.chips)
                    : summaryFromBackend((payload.chips_by_rule || {})[ruleId]);
            }
        } else {
            if (Number.isFinite(payload.count)) kpis.active = payload.count;
            if (Number.isFinite(payload.snoozed_count)) kpis.snoozed = payload.snoozed_count;
        }
        const client = clientId !== null && ruleId === null ? clientsIn(payload, clientId)[0] : null;
        if (client) {
            if (Number.isFinite(Number(client.count))) chips.active = Number(client.count);
            if (Number.isFinite(Number(client.snoozed_count))) chips.snoozed = Number(client.snoozed_count);
        }
    }
    const clients = clientsOf(payload);
    // Lo cuenta el backend antes del tope (que saca de la lista a los clientes sin ítems): contar los
    // que vinieron daría de menos con `truncated` (C-ac-fe-ahora-04). Sin el campo, backend viejo.
    kpis.clientsWithActive = payload && Number.isFinite(payload.clients_with_active)
        ? payload.clients_with_active
        : clients.filter((client) => Number(client.count) > 0).length;
    kpis.clientsTotal = payload && Number.isFinite(payload.clients_total) ? payload.clients_total : clients.length;
    return {kpis, chips};
}

/**
 * Los grupos que se ven, `[{client, active, snoozed}]`: por cliente, en el orden del backend (críticas,
 * después vigentes), con las filas del filtro y de la regla; con «Por cliente», sólo ese cliente. Un
 * cliente sin filas no aparece.
 */
export function groupsForView(payload, filter, ruleId = null, clientId = null, today = new Date()) {
    return clientsIn(payload, clientId)
        .map((client) => {
            const rows = alertsForFilter({items: itemsOf(client).filter((item) => matchesRule(item, ruleId))},
                filter, today);
            return {client, active: rows.active, snoozed: rows.snoozed};
        })
        .filter((group) => group.active.length || group.snoozed.length);
}

function activeOf(client) {
    return itemsOf(client).filter((item) => !item.snoozed);
}

/**
 * Las filas que se resolvieron mientras se miraba «Ahora» (card Fnvibexi), por cliente:
 * `Map<client_id, {client, entries: Map<id, entrada>}>`. Las entradas son las de `nextResolved`, una por
 * cliente, y su lugar es el que tenían en las filas DIBUJADAS de ese cliente (`previousGroups`, la salida de
 * `groupsWithResolved` antes de esta lectura: con las resueltas que ya había y los filtros que se veían; review
 * C-09). Se conserva el cliente dibujado: uno cuya única alerta se resolvió ya no viene en `clients`, y su fila
 * «Resuelta» sigue en pantalla.
 */
export function nextResolvedByClient(resolved, previousGroups, payload) {
    const next = new Map();
    const current = new Map(clientsOf(payload).map((client) => [String(client.client_id), client]));
    const shown = new Map((previousGroups || []).map((group) => [String(group.client.client_id), group]));
    const recoveredByClient = new Map();
    ((payload && payload.recovered) || []).forEach((row) => {
        const key = String(row.client_id);
        recoveredByClient.set(key, [...(recoveredByClient.get(key) || []), row]);
    });
    const ids = new Set([...(resolved || new Map()).keys(), ...shown.keys()]);
    ids.forEach((id) => {
        const before = resolved && resolved.get(id);
        const group = shown.get(id);
        const shownItems = group ? (group.rows || group.active || []).map((row) => (row.item ? row.item : row)) : [];
        const entries = nextResolved(before ? before.entries : null, shownItems, recoveredByClient.get(id) || [],
            activeOf(current.get(id)));
        if (entries.size) {
            next.set(id, {client: current.get(id) || (group && group.client) || before.client, entries});
        }
    });
    return next;
}

/**
 * Los grupos de la vista con las resueltas en su lugar: cada grupo suma `rows` (`withResolvedRows`). Un cliente
 * que sólo tiene resueltas entra con sus conteos en cero. Una resuelta sale con el chip, la regla y el cliente
 * con los que salía su fila.
 */
export function groupsWithResolved(groups, resolvedByClient, filter, ruleId = null, clientId = null,
                                   today = new Date()) {
    const shownOf = (id) => {
        const resolved = resolvedByClient && resolvedByClient.get(String(id));
        if (!resolved) return [];
        return [...resolved.entries.values()].filter((entry) => matchesRule(entry.item, ruleId)
            && alertsForFilter({items: [entry.item]}, filter, today).active.length > 0);
    };
    const result = groups.map((group) => ({...group, rows: withResolvedRows(group.active,
        shownOf(group.client.client_id))}));
    const present = new Set(groups.map((group) => String(group.client.client_id)));
    (resolvedByClient ? [...resolvedByClient.entries()] : []).forEach(([id, {client}]) => {
        if (present.has(id) || !matchesClient(client, clientId)) return;
        const shown = shownOf(id);
        if (!shown.length) return;
        result.push({client: {...client, count: 0, snoozed_count: 0}, active: [], snoozed: [],
            rows: withResolvedRows([], shown)});
    });
    return result;
}

/** Las vigentes de todos los clientes (las que miran el refresco y el «Recalculando…»). */
export function activeItems(payload) {
    return clientsOf(payload).flatMap(activeOf);
}

// El número de cada chip, por su clave en la URL (el mismo mapa que usa `AlertsToolbar`).
const CHIP_KEY_BY_FILTER = {
    todas: 'active',
    criticas: 'critic',
    hoy: 'today',
    'se-repiten': 'repeated',
    silenciadas: 'snoozed',
};

/** El contador de un chip en `chips` de `nowSummary`. */
export function chipCountFor(chips, filter) {
    return (chips && chips[CHIP_KEY_BY_FILTER[filter]]) || 0;
}

/** Cuántas filas dibuja la pantalla (lo que dice «Mostrando las N más graves»). */
export function shownItemsCount(payload) {
    return allItems(payload).length;
}

function rowIdentity(item) {
    const detail = (item && item.detail) || {};
    if (detail.problem_key) return `p:${detail.problem_key}`;
    const lines = Array.isArray(item.business_unit_ids) && item.business_unit_ids.length
        ? item.business_unit_ids : [item.business_unit_id];
    return ['i', item.indicator, lines.join(','), item.item_label || detail.item_label || '', item.first_seen || '']
        .join('|');
}

/**
 * Una key por fila que sigue a la ALERTA, no a su posición (review C-04 de la tanda «Control de líneas»): la
 * fila tiene estado propio (el renglón «Avisada por mail» abierto) y, con la key por índice, silenciar otra
 * fila corría la lista y el renglón abierto aparecía sobre la alerta que ocupaba ese lugar. Sale de
 * `problem_key` (la identidad del problema en el gate) o, sin ella, de indicador + líneas + ítem + inicio;
 * si dos coinciden, la segunda lleva su número. Devuelve `[{item, key}]`, en el mismo orden.
 */
export function keyedRows(items) {
    const seen = {};
    return (items || []).map((item) => {
        const identity = rowIdentity(item || {});
        seen[identity] = (seen[identity] || 0) + 1;
        return {item, key: seen[identity] > 1 ? `${identity}#${seen[identity]}` : identity};
    });
}

/** Las iniciales del avatar del grupo: «Go Fishing Perú» → «GF». */
export function clientInitials(name) {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    // Por punto de código, no por unidad UTF-16: un emoji al principio no se parte en dos mitades.
    return words.slice(0, 2).map((word) => Array.from(word)[0]).join('').toUpperCase();
}
